
import { format, parseISO } from "date-fns";
import { Building2, MapPin, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface HotelSegmentProps {
  hotelDetails: {
    name: string;
    city: string;
    checkIn?: string;
    checkOut?: string;
  };
  startDate: string;
  endDate: string;
}

const HotelSegment = ({ hotelDetails, startDate, endDate }: HotelSegmentProps) => {
  const { t } = useTranslation();
  
  // Use hotel details dates if available, otherwise fall back to props
  const checkInDate = hotelDetails.checkIn || startDate;
  const checkOutDate = hotelDetails.checkOut || endDate;
  
  const isAppleDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    console.debug("HotelSegment - User agent:", userAgent);
    const isApple = /iphone|ipad|ipod|mac|macintosh/.test(userAgent);
    console.debug("HotelSegment - Is Apple device:", isApple);
    return isApple;
  };
  
  const openGoogleMaps = () => {
    // Create the Google Maps URL with the hotel name and city
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${hotelDetails.name} ${hotelDetails.city}`
    )}`;
    
    // Open in a new tab
    window.open(mapsUrl, '_blank');
  };

  const addToCalendar = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    
    if (!checkInDate || !checkOutDate) {
      console.error("Missing check-in or check-out date");
      return;
    }
    
    const startDateTime = parseISO(checkInDate || "1970");
    const endDateTime = parseISO(checkOutDate || "1970");
    
    const title = `Stay at ${hotelDetails.name}`;
    const description = `Hotel: ${hotelDetails.name}
Address: ${hotelDetails.city}`;
    
    const deviceType = isAppleDevice() ? "Apple" : "non-Apple";
    console.debug(`HotelSegment - Detected ${deviceType} device, choosing appropriate calendar...`);
    
    if (isAppleDevice()) {
      console.debug("HotelSegment - Opening Apple Calendar");
      openAppleCalendar(title, description, startDateTime, endDateTime, hotelDetails.name);
    } else {
      console.debug("HotelSegment - Opening Google Calendar");
      openGoogleCalendar(title, description, startDateTime, endDateTime, hotelDetails.name);
    }
  };

  const openAppleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for Apple Calendar URL
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    
    // Create Apple Calendar URL with all parameters (webcal protocol)
    const calendarUrl = `webcal://p30-calendarws.icloud.com/ca/subscribe/1/${encodeURIComponent(title)}?${
      new URLSearchParams({
        title: encodeURIComponent(title),
        desc: encodeURIComponent(description),
        location: encodeURIComponent(location),
        dtstart: formattedStartDate,
        dtend: formattedEndDate
      }).toString()
    }`;
    
    console.debug("HotelSegment - Apple Calendar URL:", calendarUrl);
    
    // Open calendar URL
    window.location.href = calendarUrl;
  };

  const openGoogleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for calendar URL (remove special characters)
    const formattedStartDate = startDate.toISOString().replace(/-|:|\.\d+/g, '');
    const formattedEndDate = endDate.toISOString().replace(/-|:|\.\d+/g, '');
    
    // Create calendar URL with all parameters
    const calendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formattedStartDate}/${formattedEndDate}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&sprop=&sprop=name:`;
    
    // Open calendar URL
    window.open(calendarUrl, '_blank');
  };

  if (!checkInDate || !checkOutDate) {
    return (
      <div className="bg-travel-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-travel-600">
          <Building2 className="w-4 h-4" />
          <span className="font-semibold text-sm">{hotelDetails.name}</span>
        </div>
        <div className="text-sm text-travel-500">{hotelDetails.city}</div>
        <div className="text-xs text-travel-400 italic">
          {t("tripCard.noDateInfo", "No date information available")}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-travel-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 text-travel-600">
        <Building2 className="w-4 h-4" />
        <span className="font-semibold text-sm">
          {hotelDetails.name}
        </span>
      </div>
      <div className="text-sm text-travel-500">
        {hotelDetails.city}
      </div>
      <div className="flex justify-between text-xs text-travel-500 pt-1">
        <span>{format(parseISO(checkInDate || "1970"), 'MMM d')}</span>
        <span>→</span>
        <span>{format(parseISO(checkOutDate || "1970"), 'MMM d')}</span>
      </div>
      <div className="flex flex-col gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-2 bg-white text-travel-600 border-travel-200 hover:bg-travel-50 hover:text-travel-700"
          onClick={openGoogleMaps}
        >
          <MapPin className="mr-1 h-4 w-4" />
          {t("tripCard.viewOnMap")}
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full bg-white text-travel-600 border-travel-200 hover:bg-travel-50 hover:text-travel-700"
          onClick={addToCalendar}
        >
          <CalendarPlus className="mr-1 h-4 w-4" />
          {t("tripCard.addToCalendar", "Add to Calendar")}
        </Button>
      </div>
    </div>
  );
};

export default HotelSegment;
